import axios from "axios";
import { Alert, Box, Button, List } from "@mui/material";
import useGlobalContext from "../../../hooks/useGlobalContext";
import { useEffect, useState } from "react";
import localize from "../../../common/localize";
import { Add } from "@mui/icons-material";
import ModalBackground from "../../../components/common/ModalBackground";
import AddOrEditBranch from "./AddOrEditBranch";
import { getClearClientBranchModel } from "../../../common/EntitiesManagement";
import ErrorMessage from "../../../components/common/ErrorMessage";
import DeleteConfirmationMessage from "../../../components/common/DeleteConfirmationMessage";
import NotificationItem from "../../../components/common/NotificationItem";
import ClientBranchItem from "./ClientBranchItem";

export default function ClientBranches({ model }) {
  const {
    config,
    CLIENTS_API,
    createEntity,
    updateEntity,
    deleteEntity,
    toggleVisibleModalConfirm,
  } = useGlobalContext();
  const [branches, setBranches] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [clientBranch, setClientBranch] = useState(undefined);
  const [errorMessage, setErrorMessage] = useState(undefined);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [showDeleteConfirmationMessage, setShowDeleteConfirmationMessage] =
    useState(false);

  const getBranches = async (clientId) => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(
      `${CLIENTS_API}/${clientId}/branch`,
      requestConfig
    );
    setBranches(data?.data?.Data);
  };

  useEffect(() => {
    if (model?.Id > 0) {
      getBranches(model?.Id);
    }
  }, [model?.Id]);

  const handleAddBtn = () => {
    setClientBranch(getClearClientBranchModel());
    setShowModal(true);
  };

  const updateRequest = async () => {
    const updateResult = await updateEntity(
      `${`${CLIENTS_API}/${model?.Id}/branch/${clientBranch.Id}`}`,
      {
        ...clientBranch,
        ClientId: model?.Id,
      },
      config
    );
    validateOperationResult(updateResult, false);
  };

  const createRequest = async () => {
    const updateResult = await createEntity(
      `${`${CLIENTS_API}/${model?.Id}/branch`}`,
      {
        ...clientBranch,
        ClientId: model?.Id,
      },
      config
    );
    validateOperationResult(updateResult, false);
  };

  const validateOperationResult = (result, isDelete) => {
    setClientBranch(getClearClientBranchModel());
    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorMessage(true);
      return;
    }

    getBranches(model?.Id);
  };

  const createOrUpdateClientBranch = async () => {
    if (clientBranch?.Id > 0) {
      await updateRequest();
    } else {
      await createRequest();
    }

    setShowModal(false);
    setClientBranch(getClearClientBranchModel());
  };

  const cancelClientBranchModification = () => {
    setClientBranch(getClearClientBranchModel());
    setShowModal(false);
  };

  const disableSaveChanges = () =>
    !clientBranch.Name ||
    clientBranch.Name.replaceAll(" ", "").length <= 0 ||
    !clientBranch.PhoneNumber ||
    clientBranch.PhoneNumber.replaceAll(" ", "").length <= 0 ||
    !clientBranch.Address ||
    clientBranch.Address.replaceAll(" ", "").length <= 0 ||
    clientBranch.StateId <= 0 ||
    clientBranch.CityId <= 0;

  const deleteBranch = async () => {
    const deleteResult = await deleteEntity(
      `${`${CLIENTS_API}/${model?.Id}/branch/${clientBranch?.Id}`}`,
      config
    );
    setShowDeleteConfirmationMessage(false);
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  return (
    <Box sx={{ width: "100%" }}>
      {showDeleteConfirmationMessage && (
        <DeleteConfirmationMessage
          entityName={clientBranch.Name}
          entityType={localize.branch}
          onClickForCancel={() => {
            setShowDeleteConfirmationMessage(false);
            toggleVisibleModalConfirm();
            setClientBranch(getClearClientBranchModel());
          }}
          onClickForConfirm={() => {
            toggleVisibleModalConfirm();
            deleteBranch();
          }}
        />
      )}

      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorMessage}
          setShowModal={setShowErrorMessage}
        />
      )}

      {showModal && (
        <ModalBackground
          showModal={showModal}
          setShowModal={setShowModal}
          title={
            clientBranch?.Id > 0
              ? `${localize.edit_branch} #${clientBranch?.Id}`
              : localize.new_branch
          }
          Component={
            <AddOrEditBranch model={clientBranch} setModel={setClientBranch} />
          }
          onClickToSubmit={createOrUpdateClientBranch}
          onClickToCancel={cancelClientBranchModification}
          validators={disableSaveChanges()}
        />
      )}
      <section className="flex w-full gap-2 mb-2 items-center">
        <div style={{ flex: 1 }}>
          <Alert severity="info" className="w-full">
            <label className="text-xs">{localize.info_client_branches}</label>
          </Alert>
        </div>

        <Box>
          <Button
            size="small"
            variant="contained"
            color="success"
            className="app_general_button_data app_success_button btn-sm"
            onClick={handleAddBtn}
          >
            <span className="mr-2">{localize.add}</span>
            <Add className="text-lg" />
          </Button>
        </Box>
      </section>

      {
        <List className="flex flex-col gap-2">
          {branches?.length > 0 ? (
            branches?.map((item) => (
              <ClientBranchItem
                branch={item}
                onEdit={() => {
                  setClientBranch({
                    ...item,
                    StateId: item?.State?.Id,
                    CityId: item?.City?.Id,
                    ClientId: model?.Id,
                  });
                  setShowModal(true);
                }}
                onDelete={() => {
                  setClientBranch(item);
                  setShowDeleteConfirmationMessage(true);
                  toggleVisibleModalConfirm();
                }}
              />
            ))
          ) : (
            <NotificationItem notification={{ message: localize.no_data }} />
          )}
        </List>
      }
    </Box>
  );
}
